/**
 * El panel de arriba de Ingresos y gastos: la CONCILIACIÓN del mes, fila a fila.
 *
 * UNA TABLA, en el orden en que se cuadra a mano: nº, fecha, concepto, importe, saldo, estado,
 * proveedor, inmueble, soporte documental y observaciones. Cada fila es un movimiento del mes
 * —un cargo o un abono del banco, un gasto apuntado, un cobro fuera del banco— o algo que se
 * esperaba y no ha llegado. El estado dice si está cerrada:
 *
 *  · Completo (verde) — cuadra con el banco y tiene su papel.
 *  · Completo sin justificar (azul) — está en el banco, se sabe qué es, falta el papel.
 *  · Pendiente (naranja) — le falta algo: el cargo, revisarlo, saber de quién es, o llegar.
 *
 * Arriba se elige el mes y el año; las filas se filtran por estado y por inmueble. Pulsar una
 * fila abre lo que corresponde: el apunte para corregirlo, el movimiento del banco para
 * resolverlo, o «Lo que falta» si es algo que no ha llegado.
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { GASTOS_GENERALES_ID } from '@naar/shared';
import type { BankMovement, LedgerEntry, LedgerMonthResponse } from '@naar/shared';
import { MONTHLY_CLOSE_STATUS_LABELS, MONTH_NAMES_ES } from '@naar/shared';
import { Badge, type BadgeTone } from '../../components/Badge';
import { Icon } from '../../components/Icon';
import { formatEurCents, formatShortDate } from '../../lib/format';
import { useBreakpoint } from '../../lib/useBreakpoint';
import { useDocumentViewer } from '../../components/overlay/DocumentViewer';
import {
  REPARTIDO_ID,
  esDelInmueble,
  filasDelMes,
  type EstadoFila,
  type FilaConciliacion,
} from './reconciliation';
import {
  BotonMenuFila,
  EditorCelda,
  LapizCelda,
  MenuFila,
  accionesDeFila,
  esCargoPendiente,
  puedeEditar,
  subirDocumentoDeFila,
  type CampoEditable,
} from './RowEditor';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../../api/client';
import { toast } from '../../components/feedback/Toaster';
import { avisarReapertura } from './MissingPanel';
import styles from './ReconciliationPanel.module.css';

const TONO: Record<LedgerMonthResponse['close']['status'], BadgeTone> = {
  abierto: 'neutral',
  incompleto: 'warning',
  conciliado: 'success',
};

const ESTADO: Record<EstadoFila, { label: string; clase: string }> = {
  completo: { label: 'Completo', clase: styles.estadoCompleto ?? '' },
  sin_justificar: { label: 'Completo sin justificar', clase: styles.estadoSinJustificar ?? '' },
  pendiente: { label: 'Pendiente', clase: styles.estadoPendiente ?? '' },
};

/** `dudas`: los movimientos pendientes en los que la IA ha dejado una duda. */
/**
 * `general`: los gastos generales (de ningún inmueble). `cambios`: lo que cambió la IA. `avisos`:
 * lo ya resuelto que la IA cree que está mal, sin haberlo cambiado.
 */
type FiltroEstado = 'todos' | EstadoFila | 'dudas' | 'general' | 'cambios' | 'avisos';

interface ReconciliationPanelProps {
  month: string;
  data: LedgerMonthResponse;
  canManage: boolean;
  /** Subir extracto, Conciliar, Reabrir: los pone la página, que es la que tiene las mutaciones. */
  actions?: ReactNode;
  /** El selector de mes y año. */
  period?: ReactNode;
  /** Descargas del mes y del año, en el pie. */
  downloads?: ReactNode;
  onShowMissing: () => void;
  onOpenEntry: (entry: LedgerEntry, propertyName: string) => void;
  onOpenMovement: (movement: BankMovement) => void;
  onAddManual: () => void;
  /**
   * «Todo»: los meses del año ya cargados, en orden. Con esto la tabla enseña el año entero, y
   * pulsar una fila lleva a su mes (es donde se resuelve) con ella abierta.
   */
  year?: { months: LedgerMonthResponse[]; loading: boolean } | undefined;
  onOpenInMonth?: (month: string, fila: FilaConciliacion) => void;
  /** Pulsar una fila abre su ficha (ver `FilaDrawer`). Sin esto, el panel de antes. */
  onOpenFila?: (fila: FilaConciliacion) => void;
  /** La franja de la conciliación con IA; recibe cómo filtrar la tabla a las dudas. */
  aiBanner?: (verDudas: () => void) => ReactNode;
}

/** Para buscar sin que importen mayúsculas ni tildes. */
function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function euros(value: number): string {
  return value < 0 ? `-${formatEurCents(Math.abs(value))}` : formatEurCents(value);
}

export function ReconciliationPanel({
  month,
  data,
  canManage,
  actions,
  period,
  downloads,
  onShowMissing,
  onOpenEntry,
  onOpenMovement,
  onAddManual,
  year,
  onOpenInMonth,
  onOpenFila,
  aiBanner,
}: ReconciliationPanelProps) {
  const [filtro, setFiltro] = useState<FiltroEstado>('todos');
  /*
   * En el móvil, diez columnas no caben: la tabla se cambia por una ficha por movimiento, con lo
   * que se mira de un vistazo (concepto, importe y estado) arriba y el resto debajo, pequeño.
   */
  const movil = useBreakpoint() === 'movil';
  const panelRef = useRef<HTMLElement>(null);
  const [alto, setAlto] = useState<number | null>(null);

  /*
   * LA TARJETA LLEGA HASTA EL FINAL DE LA PANTALLA y la tabla se desplaza DENTRO, con los
   * títulos fijos. Con cien movimientos, que la página entera bajara se llevaba por delante la
   * cabecera con el mes y los filtros, que es lo que se usa mientras se repasa.
   *
   * El alto se mide (la posición de la tarjeta depende de los avisos que haya encima) y solo en
   * pantallas anchas: en un móvil, un scroll dentro de otro es una trampa para el dedo.
   */
  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    const zonaTabla = () => panel.querySelector<HTMLElement>(`:scope > .${styles.tableWrap}`);
    const medir = () => {
      if (window.innerWidth < 900) {
        setAlto(null);
        return;
      }
      const top = panel.getBoundingClientRect().top + window.scrollY;
      // Se descuenta el relleno inferior de la zona principal: así la página no baja ni un píxel.
      const main = panel.closest('main');
      const abajo = main ? parseFloat(getComputedStyle(main).paddingBottom) || 24 : 24;
      const hastaElFinal = Math.floor(window.innerHeight - top - abajo);
      /*
       * PERO LA TABLA NUNCA SE QUEDA EN UNA FILA. Con los avisos de arriba («Hay meses sin
       * cuadrar», «Al conciliar faltaba…») y la franja de la IA dentro, la tarjeta empieza tan
       * abajo que hasta el final de la pantalla solo cabía una fila. Ahora la tabla tiene
       * siempre casi una pantalla de alto (o todas sus filas, si son menos) y, si no cabe, la
       * página baja: se desplaza hasta la tarjeta y la tabla se recorre dentro.
       */
      const tabla = zonaTabla();
      const resto = tabla ? panel.offsetHeight - tabla.offsetHeight : 380;
      const contenido = tabla ? tabla.scrollHeight : 0;
      const minimoTabla = Math.min(Math.max(contenido, 120), Math.max(420, window.innerHeight - 220));
      setAlto(Math.max(hastaElFinal, Math.ceil(resto + minimoTabla)));
    };
    medir();
    window.addEventListener('resize', medir);
    const observer = new ResizeObserver(medir);
    if (panel.parentElement) observer.observe(panel.parentElement);
    // La zona de la tabla cambia de alto cuando aparece algo encima (la franja de la IA, los filtros).
    let observada = zonaTabla();
    if (observada) observer.observe(observada);
    const cambios = new MutationObserver(() => {
      const nueva = zonaTabla();
      if (nueva !== observada) {
        if (observada) observer.unobserve(observada);
        if (nueva) observer.observe(nueva);
        observada = nueva;
      }
      medir();
    });
    cambios.observe(panel, { childList: true });
    return () => {
      window.removeEventListener('resize', medir);
      observer.disconnect();
      cambios.disconnect();
    };
  }, []);
  const [inmuebleId, setInmuebleId] = useState('');
  const mes = MONTH_NAMES_ES[Number(month.slice(5, 7)) - 1] ?? month;
  const { close, totals } = data;

  /*
   * Con «Todo», las filas de cada mes una detrás de otra. La clave lleva el mes delante: dos meses
   * pueden tener el mismo gasto fijo pendiente, y con la misma clave React los confundiría.
   */
  const mesDeFila = useMemo(() => new Map<string, string>(), [year]);
  const tabla = useMemo(() => {
    if (!year) return filasDelMes(data);
    mesDeFila.clear();
    const filas: FilaConciliacion[] = [];
    let saldoReal = false;
    for (const mesData of year.months) {
      const parcial = filasDelMes(mesData);
      saldoReal ||= parcial.saldoReal;
      for (const fila of parcial.filas) {
        const key = `${mesData.month}|${fila.key}`;
        mesDeFila.set(key, mesData.month);
        filas.push({ ...fila, key });
      }
    }
    return { filas, saldoReal };
  }, [data, year, mesDeFila]);
  const netoAnio = year
    ? year.months.reduce((suma, mesData) => suma + Number(mesData.totals.net), 0)
    : null;
  const numeros = useMemo(() => new Map(tabla.filas.map((fila, index) => [fila.key, index + 1])), [tabla]);
  const cuenta = useMemo(() => {
    const result = { completo: 0, sin_justificar: 0, pendiente: 0, dudas: 0, general: 0, cambios: 0, avisos: 0 };
    for (const fila of tabla.filas) {
      result[fila.estado] += 1;
      if (fila.duda) result.dudas += 1;
      if (fila.inmueble?.id === GASTOS_GENERALES_ID) result.general += 1;
      if (fila.cambioIa) result.cambios += 1;
      if (fila.avisoIa) result.avisos += 1;
    }
    return result;
  }, [tabla]);
  // Si las dudas (o los cambios o avisos de la IA) se resuelven todos con el filtro puesto, se vuelve a ver todo.
  useEffect(() => {
    if (
      (filtro === 'dudas' && cuenta.dudas === 0) ||
      (filtro === 'cambios' && cuenta.cambios === 0) ||
      (filtro === 'avisos' && cuenta.avisos === 0)
    ) {
      setFiltro('todos');
    }
  }, [filtro, cuenta.dudas, cuenta.cambios, cuenta.avisos]);

  const inmuebles = useMemo(() => {
    const byId = new Map<string, string>();
    for (const fila of tabla.filas) {
      // Un cargo repartido sale en el filtro de cada uno de sus pisos, no como «Varios».
      if (fila.inmueble?.id && fila.inmueble.id !== REPARTIDO_ID) byId.set(fila.inmueble.id, fila.inmueble.name);
      for (const parte of fila.reparto ?? []) byId.set(parte.propertyId, parte.propertyName);
    }
    return [...byId].sort((a, b) => a[1].localeCompare(b[1], 'es'));
  }, [tabla]);

  /*
   * FILTROS: estado e inmueble (arriba), y en la segunda fila proveedor, buscar en el concepto,
   * buscar en las observaciones y un abanico de importe. Se combinan todos (Y). La búsqueda no
   * distingue mayúsculas ni tildes: «limpieza» encuentra «LIMPIEZA» y «Gómez» encuentra «GOMEZ».
   */
  const [proveedor, setProveedor] = useState('');
  const [buscaConcepto, setBuscaConcepto] = useState('');
  const [buscaNotas, setBuscaNotas] = useState('');
  /** El abanico de importe elegido, en euros enteros; `null` es «todos». */
  const [rango, setRango] = useState<[number, number] | null>(null);

  const proveedores = useMemo(() => {
    const unicos = new Set<string>();
    for (const fila of tabla.filas) if (fila.proveedor) unicos.add(fila.proveedor);
    return [...unicos].sort((a, b) => a.localeCompare(b, 'es'));
  }, [tabla]);

  // Los extremos del abanico salen de las filas: del cargo más grande al ingreso más grande.
  const limites = useMemo<[number, number] | null>(() => {
    const importes = tabla.filas.map((fila) => fila.importe).filter((v): v is number => v !== null);
    if (importes.length === 0) return null;
    const min = Math.floor(Math.min(...importes));
    const max = Math.ceil(Math.max(...importes));
    return min === max ? null : [min, max];
  }, [tabla]);
  // Al cambiar de mes los extremos cambian: un abanico fuera de ellos se recoloca.
  const rangoEfectivo: [number, number] | null =
    rango && limites
      ? [Math.max(limites[0], Math.min(rango[0], limites[1])), Math.min(limites[1], Math.max(rango[1], limites[0]))]
      : null;
  const rangoActivo = Boolean(
    rangoEfectivo && limites && (rangoEfectivo[0] > limites[0] || rangoEfectivo[1] < limites[1]),
  );

  const qConcepto = normalizar(buscaConcepto);
  const qNotas = normalizar(buscaNotas);
  const filas = tabla.filas.filter(
    (fila) =>
      (filtro === 'todos' ||
        (filtro === 'dudas'
          ? Boolean(fila.duda)
          : filtro === 'general'
            ? fila.inmueble?.id === GASTOS_GENERALES_ID
            : filtro === 'cambios'
              ? Boolean(fila.cambioIa)
              : filtro === 'avisos'
                ? Boolean(fila.avisoIa)
                : fila.estado === filtro)) &&
      (!inmuebleId || esDelInmueble(fila, inmuebleId)) &&
      (!proveedor || fila.proveedor === proveedor) &&
      (!qConcepto || normalizar(fila.concepto).includes(qConcepto)) &&
      (!qNotas || normalizar(fila.observaciones ?? '').includes(qNotas)) &&
      (!rangoActivo ||
        (fila.importe !== null && fila.importe >= rangoEfectivo![0] && fila.importe <= rangoEfectivo![1])),
  );
  const hayFiltros = Boolean(
    filtro !== 'todos' || inmuebleId || proveedor || qConcepto || qNotas || rangoActivo,
  );
  function limpiarFiltros() {
    setFiltro('todos');
    setInmuebleId('');
    setProveedor('');
    setBuscaConcepto('');
    setBuscaNotas('');
    setRango(null);
  }

  const total = tabla.filas.length;
  const pct = total === 0 ? null : Math.floor((cuenta.completo / total) * 100);

  function abrir(fila: FilaConciliacion) {
    const mesFila = mesDeFila.get(fila.key);
    if (year && mesFila && onOpenInMonth) {
      onOpenInMonth(mesFila, fila);
      return;
    }
    if (fila.prevista) {
      onShowMissing();
      return;
    }
    if (onOpenFila) {
      onOpenFila(fila);
      return;
    }
    if (fila.entry) onOpenEntry(fila.entry.entry, fila.entry.propertyName);
    else if (fila.movement) onOpenMovement(fila.movement);
  }

  /*
   * VISTA AMPLIA: la misma tarjeta —cabecera, mes, filtros, tabla y pie— a casi toda la
   * pantalla. Con veinte movimientos la tabla de la página se queda en un puñado de filas; aquí
   * se ven de golpe. Los filtros son los mismos (el mismo estado), así que al cerrarla todo
   * sigue como se dejó. Los paneles que abre una fila salen por encima.
   */
  const [amplia, setAmplia] = useState(false);
  const botonAmpliaRef = useRef<HTMLButtonElement>(null);
  const cerrarAmpliaRef = useRef<HTMLButtonElement>(null);
  const cerrarAmplia = useCallback(() => {
    setAmplia(false);
    /*
     * Los filtros de la vista amplia se quedan en ella: en la página no se ven, y una tabla
     * filtrada sin filtros a la vista parecería que le faltan filas. El de estado sí sigue,
     * porque está en las dos.
     */
    setInmuebleId('');
    setProveedor('');
    setBuscaConcepto('');
    setBuscaNotas('');
    setRango(null);
    // Devolver el foco a quien la abrió, como un panel.
    setTimeout(() => botonAmpliaRef.current?.focus(), 0);
  }, []);
  useEffect(() => {
    if (!amplia) return undefined;
    const previo = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const timer = setTimeout(() => cerrarAmpliaRef.current?.focus(), 30);
    // En burbuja y en `window`: los paneles y el editor de celda escuchan antes y se lo quedan.
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') cerrarAmplia();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previo;
      clearTimeout(timer);
      window.removeEventListener('keydown', onKey);
    };
  }, [amplia, cerrarAmplia]);
  // En el móvil no hay vista amplia: la pantalla ya es toda la tarjeta.
  useEffect(() => {
    if (movil) setAmplia(false);
  }, [movil]);

  /* --- Editar una celda con su lápiz ------------------------------------------------ */
  const [editando, setEditando] = useState<{ fila: FilaConciliacion; campo: CampoEditable; anchor: HTMLElement } | null>(
    null,
  );
  const cerrarEditor = useCallback(() => setEditando(null), []);
  /* El menú «⋯» de una fila: borrar, quitar el documento, volver a pendiente. */
  const [menu, setMenu] = useState<{ fila: FilaConciliacion; anchor: HTMLElement } | null>(null);
  const cerrarMenu = useCallback(() => setMenu(null), []);
  // Todos los inmuebles, no solo los que salen en la tabla: el gasto puede ser de otro.
  const todosLosInmuebles = useMemo(
    () => data.properties.map((property) => ({ id: property.id, name: property.name })),
    [data.properties],
  );

  /*
   * SOPORTE DOCUMENTAL: en vez de lápiz, la acción que toca. Con papel, un OJO que lo abre en el
   * visor; sin papel, una FLECHA que abre el selector de ficheros y lo sube a donde corresponda.
   * Un cargo pendiente sin inmueble necesita saber de qué piso es: ahí la flecha abre la
   * tarjetita con el desplegable.
   */
  const visor = useDocumentViewer();
  const queryClient = useQueryClient();
  const ficheroRef = useRef<HTMLInputElement>(null);
  const [subirA, setSubirA] = useState<FilaConciliacion | null>(null);
  const subir = useMutation({
    mutationFn: ({ fila, file }: { fila: FilaConciliacion; file: File }) =>
      subirDocumentoDeFila(fila, file, fila.inmueble?.id ?? ''),
    onSuccess: async (result, { fila }) => {
      await queryClient.invalidateQueries({ queryKey: ['ledger'] });
      toast.success('Documento subido.');
      if (result.reopened) avisarReapertura(mesDeFila.get(fila.key) ?? month);
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'No se ha podido subir el documento.'),
  });
  const subiendoKey = subir.isPending ? subir.variables?.fila.key : undefined;

  function pedirFichero(fila: FilaConciliacion, anchor: HTMLElement) {
    if (esCargoPendiente(fila) && !fila.inmueble?.id) {
      setEditando({ fila, campo: 'soporte', anchor });
      return;
    }
    setSubirA(fila);
    ficheroRef.current?.click();
  }

  function celdaSoporte(fila: FilaConciliacion) {
    const documento = fila.soporte && fila.soporte !== 'extracto' ? fila.soporte : null;
    const puedeSubir = canManage && puedeEditar(fila, 'soporte');
    const subiendo = subiendoKey === fila.key;
    return (
      <td className={styles.soporteCol}>
        <div className={styles.celdaEditable}>
          <div className={styles.celdaValor}>
            {fila.soporte === 'extracto' ? (
              <span className={styles.muted}>Extracto bancario</span>
            ) : documento ? (
              <DocLink document={documento} />
            ) : (
              <span className={styles.missingDoc}>{subiendo ? 'Subiendo…' : 'Sin documento'}</span>
            )}
          </div>
          {documento ? (
            <button
              type="button"
              className={styles.accionDoc}
              title={`Ver ${documento.name}`}
              aria-label={`Ver ${documento.name}`}
              onClick={(event) => {
                event.stopPropagation();
                visor.open({ id: documento.id, name: documento.name });
              }}
              onKeyDown={(event) => event.stopPropagation()}
            >
              <Icon name="eye" size={14} strokeWidth={2} />
            </button>
          ) : puedeSubir ? (
            <button
              type="button"
              className={`${styles.accionDoc} ${styles.accionSubir}`}
              title={fila.soporte === 'extracto' ? 'Subir un justificante' : 'Subir el documento'}
              aria-label={fila.soporte === 'extracto' ? 'Subir un justificante' : 'Subir el documento'}
              disabled={subiendo}
              aria-busy={subiendo || undefined}
              onClick={(event) => {
                event.stopPropagation();
                pedirFichero(fila, event.currentTarget);
              }}
              onKeyDown={(event) => event.stopPropagation()}
            >
              <Icon name="upload" size={14} strokeWidth={2.2} />
            </button>
          ) : null}
        </div>
      </td>
    );
  }

  function celda(fila: FilaConciliacion, campo: CampoEditable, valor: ReactNode, className?: string) {
    const editable = canManage && puedeEditar(fila, campo);
    return (
      <td className={className}>
        <div className={styles.celdaEditable}>
          <div className={styles.celdaValor}>{valor}</div>
          {editable && (
            <LapizCelda
              campo={campo}
              activo={editando?.fila.key === fila.key && editando.campo === campo}
              onOpen={(anchor) => setEditando({ fila, campo, anchor })}
            />
          )}
        </div>
      </td>
    );
  }

  const cabecera = (enAmplia: boolean) => (
    <>
      <div className={styles.head}>
        <div className={styles.headMain}>
          <div className={styles.eyebrowRow}>
            <span className={styles.eyebrow}>Conciliación</span>
            {!year && <Badge tone={TONO[close.status]}>{MONTHLY_CLOSE_STATUS_LABELS[close.status]}</Badge>}
            {enAmplia && (
              <span className={styles.ampliaMes}>
                {year ? `Todo ${month.slice(0, 4)}` : `${mes} ${month.slice(0, 4)}`}
              </span>
            )}
          </div>
          <p className={styles.summary}>
            {year ? `Todo ${month.slice(0, 4)} · ` : ''}
            {year?.loading
              ? 'Cargando los meses…'
              : total === 0
                ? year
                  ? 'Todavía no hay movimientos este año.'
                  : 'Todavía no hay movimientos este mes.'
                : `${total} ${total === 1 ? 'movimiento' : 'movimientos'} · ${pct}% completo`}
            {!year && close.status === 'conciliado' && close.reconciledBy
              ? ` · conciliado por ${close.reconciledBy}${
                  close.reconciledAt ? ` el ${formatShortDate(close.reconciledAt.slice(0, 10))}` : ''
                }`
              : ''}
          </p>
        </div>
        {(actions || enAmplia) && (
          <div className={styles.actions}>
            {actions}
            {enAmplia && (
              <button
                ref={cerrarAmpliaRef}
                type="button"
                className={styles.cerrarAmplia}
                onClick={cerrarAmplia}
                aria-label="Cerrar la vista amplia"
                title="Cerrar (Esc)"
              >
                <Icon name="close" size={18} strokeWidth={2} />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Año y mes, en su propia fila: doce botones no caben junto al título. */}
      {period && <div className={styles.period}>{period}</div>}
    </>
  );

  const filtros = (enAmplia: boolean) => (
    <>
    <div className={styles.toolbar}>
      <div className={styles.segmented} role="group" aria-label="Filtrar por estado">
        {(
          [
            ['todos', 'Todos', total],
            ['completo', 'Completo', cuenta.completo],
            ['sin_justificar', 'Sin justificar', cuenta.sin_justificar],
            ['pendiente', 'Pendiente', cuenta.pendiente],
            // Solo si las hay: un filtro que siempre da cero es ruido.
            ...(cuenta.dudas > 0 ? ([['dudas', 'Dudas IA', cuenta.dudas]] as const) : []),
            ...(cuenta.avisos > 0 ? ([['avisos', 'Avisos IA', cuenta.avisos]] as const) : []),
            ...(cuenta.cambios > 0 ? ([['cambios', 'Cambios IA', cuenta.cambios]] as const) : []),
            // Los gastos de ningún inmueble: la cuota del préstamo, las comisiones, los traspasos…
            ['general', 'Gasto general', cuenta.general],
          ] as const
        ).map(([id, label, n]) => (
          <button
            key={id}
            type="button"
            className={filtro === id ? `${styles.segment} ${styles.segmentActive}` : styles.segment}
            aria-pressed={filtro === id}
            onClick={() => setFiltro(id)}
          >
            {id === 'dudas' || id === 'cambios' || id === 'avisos' ? (
              <span className={styles.dudaPunto} aria-hidden="true">
                {id === 'dudas' ? '?' : id === 'avisos' ? '!' : '↻'}
              </span>
            ) : id === 'general' ? (
              <span className={styles.dot} aria-hidden="true" />
            ) : (
              id !== 'todos' && <span className={`${styles.dot} ${ESTADO[id].clase}`} aria-hidden="true" />
            )}
            {label}
            <span className={styles.segmentCount}>{n}</span>
          </button>
        ))}
      </div>

      <div className={styles.toolbarRight}>
        {canManage && (
          <button type="button" className={styles.toolButton} onClick={onAddManual}>
            <Icon name="plus" size={14} strokeWidth={2.4} />
            Apunte sin factura
          </button>
        )}
        {!movil && !enAmplia && (
          <button
            ref={botonAmpliaRef}
            type="button"
            className={styles.toolButton}
            onClick={() => setAmplia(true)}
            title="Ver la tabla en grande"
          >
            <Icon name="expand" size={14} strokeWidth={2.2} />
            Vista amplia
          </button>
        )}
      </div>
    </div>

    {/* Segunda fila: buscar y acotar. Solo en la vista amplia: en la página, la tabla ya es baja. */}
    {enAmplia && (
    <div className={styles.filterBar} role="search" aria-label="Filtrar movimientos">
      <label className={styles.searchBox}>
        <Icon name="search" size={14} strokeWidth={2.2} />
        <input
          type="search"
          value={buscaConcepto}
          onChange={(event) => setBuscaConcepto(event.target.value)}
          placeholder="Buscar concepto…"
          aria-label="Buscar por concepto"
        />
      </label>
      <label className={styles.searchBox}>
        <Icon name="search" size={14} strokeWidth={2.2} />
        <input
          type="search"
          value={buscaNotas}
          onChange={(event) => setBuscaNotas(event.target.value)}
          placeholder="Buscar en observaciones…"
          aria-label="Buscar en observaciones"
        />
      </label>
      <select
        className={styles.select}
        value={proveedor}
        onChange={(event) => setProveedor(event.target.value)}
        aria-label="Filtrar por proveedor"
        disabled={proveedores.length === 0}
      >
        <option value="">Todos los proveedores</option>
        {proveedores.map((nombre) => (
          <option key={nombre} value={nombre}>
            {nombre}
          </option>
        ))}
      </select>
      {inmuebles.length > 1 && (
        <select
          className={styles.select}
          value={inmuebleId}
          onChange={(event) => setInmuebleId(event.target.value)}
          aria-label="Filtrar por inmueble"
        >
          <option value="">Todos los inmuebles</option>
          {inmuebles.map(([id, name]) => (
            <option key={id} value={id}>
              {name}
            </option>
          ))}
        </select>
      )}
      {limites && (
        <RangoImporte
          limites={limites}
          valor={rangoEfectivo ?? limites}
          activo={rangoActivo}
          onChange={(nuevo) => setRango(nuevo)}
        />
      )}
      {hayFiltros && (
        <div className={styles.filterInfo}>
          <span>
            {filas.length} de {total}
          </span>
          <button type="button" className={styles.clearFilters} onClick={limpiarFiltros}>
            Quitar filtros
          </button>
        </div>
      )}
    </div>
    )}
    </>
  );

  const tablaJsx = movil ? (
    <MobileList filas={filas} total={total} numeros={numeros} onOpen={abrir} />
  ) : (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.num}>Nº</th>
            <th>Fecha</th>
            <th>Concepto</th>
            <th className={styles.right}>Importe</th>
            <th
              className={styles.right}
              title={
                tabla.saldoReal
                  ? 'Saldo de la cuenta tras cada movimiento del banco, según el extracto.'
                  : 'Acumulado del mes: el extracto no trae saldo final.'
              }
            >
              Saldo
            </th>
            <th className={styles.estadoCol}>Estado</th>
            <th>Proveedor</th>
            <th>Inmueble</th>
            <th>Quién paga</th>
            <th>Soporte documental</th>
            <th>Observaciones</th>
            <th className={styles.menuCol} aria-label="Acciones" />
          </tr>
        </thead>
        <tbody>
          {filas.length === 0 ? (
            <tr>
              <td colSpan={12} className={styles.empty}>
                {total === 0
                  ? 'No hay movimientos este mes. Suelta los tickets en su inmueble o sube el extracto del banco.'
                  : 'Ninguna fila con este filtro.'}
              </td>
            </tr>
          ) : (
            filas.map((fila) => {
              const clicable = Boolean(fila.entry || fila.movement || fila.payment || fila.prevista);
              return (
                <tr
                  key={fila.key}
                  className={[clicable ? styles.clickable : '', fila.prevista ? styles.prevista : '']
                    .filter(Boolean)
                    .join(' ')}
                  {...(clicable
                    ? {
                        tabIndex: 0,
                        onClick: () => abrir(fila),
                        onKeyDown: (event: KeyboardEvent) => {
                          if (event.key === 'Enter' && event.target === event.currentTarget) abrir(fila);
                        },
                      }
                    : {})}
                >
                  <td className={styles.num}>{numeros.get(fila.key)}</td>
                  <td className={styles.nowrap}>{fila.fecha ? formatShortDate(fila.fecha) : '—'}</td>
                  <td className={styles.concept}>{fila.concepto}</td>
                  <td
                    className={[
                      styles.right,
                      styles.nowrap,
                      styles.amount,
                      fila.importe !== null && fila.importe > 0 ? styles.income : '',
                    ].join(' ')}
                  >
                    {fila.importe === null ? '—' : euros(fila.importe)}
                  </td>
                  <td className={`${styles.right} ${styles.nowrap} ${styles.muted}`}>
                    {fila.saldo === null ? '—' : euros(fila.saldo)}
                  </td>
                  <td className={styles.estadoCol}>
                    <span className={`${styles.estado} ${ESTADO[fila.estado].clase}`}>
                      <span className={styles.dot} aria-hidden="true" />
                      {ESTADO[fila.estado].label}
                    </span>
                  </td>
                  {celda(fila, 'proveedor', <span className={styles.clip}>{fila.proveedor ?? '—'}</span>)}
                  {celda(fila, 'inmueble', <span className={styles.clip}>{nombreDelInmueble(fila)}</span>)}
                  {celda(fila, 'pagador', <Pagador fila={fila} />)}
                  {celdaSoporte(fila)}
                  {celda(fila, 'observaciones', <Observaciones fila={fila} />, styles.notes)}
                  <td className={styles.menuCol}>
                    {canManage && accionesDeFila(fila).length > 0 && (
                      <BotonMenuFila
                        activo={menu?.fila.key === fila.key}
                        onOpen={(anchor) => setMenu({ fila, anchor })}
                      />
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );

  const pie = (
    <div className={styles.footer}>
      <div className={styles.net}>
        <span className={styles.netLabel}>{year ? 'Neto del año' : 'Neto del mes'}</span>
        <strong className={Number(netoAnio ?? totals.net) < 0 ? styles.negative : styles.positive}>
          {formatEurCents(netoAnio ?? totals.net)}
        </strong>
      </div>
      <div className={styles.footerActions}>
        {downloads}
        {!year && (totals.gapCount > 0 || data.bank.missingStatement) ? (
          <button type="button" className={styles.missingLink} onClick={onShowMissing}>
            Ver lo que falta
            <Icon name="chevron-right" size={14} strokeWidth={2.2} />
          </button>
        ) : null}
      </div>
    </div>
  );

  return (
    <>
      <section
        ref={panelRef}
        className={alto ? `${styles.panel} ${styles.panelFill}` : styles.panel}
        style={alto ? { height: alto } : undefined}
        aria-label={`Conciliación de ${mes} de ${month.slice(0, 4)}`}
      >
        {cabecera(false)}
        {filtros(false)}
        {aiBanner?.(() => setFiltro('dudas'))}
        {/* Con la vista amplia abierta, la tabla de la página no se repite debajo. */}
        {amplia ? <div className={styles.tableWrap} /> : tablaJsx}
        {pie}
      </section>

      {amplia &&
        createPortal(
          <div
            className={styles.ampliaVelo}
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) cerrarAmplia();
            }}
          >
            <section
              className={`${styles.panel} ${styles.panelFill} ${styles.amplia}`}
              role="dialog"
              aria-modal="true"
              aria-label={`Conciliación de ${mes} de ${month.slice(0, 4)}, vista amplia`}
            >
              {cabecera(true)}
              {filtros(true)}
              {tablaJsx}
              {pie}
            </section>
          </div>,
          document.body,
        )}

      <input
        ref={ficheroRef}
        type="file"
        accept="application/pdf,image/*"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file && subirA) subir.mutate({ fila: subirA, file });
          setSubirA(null);
        }}
      />

      {menu && (
        <MenuFila key={menu.fila.key} fila={menu.fila} anchor={menu.anchor} onClose={cerrarMenu} />
      )}

      {editando && (
        <EditorCelda
          key={`${editando.fila.key}|${editando.campo}`}
          fila={editando.fila}
          campo={editando.campo}
          anchor={editando.anchor}
          month={mesDeFila.get(editando.fila.key) ?? month}
          properties={todosLosInmuebles}
          tenants={data.bank.tenantOptions}
          onClose={cerrarEditor}
        />
      )}
    </>
  );
}

/**
 * El abanico de importe: dos tiradores sobre la misma barra, del cargo más grande del mes al
 * ingreso más grande. Los importes van con signo (los gastos en negativo), así que «solo
 * gastos» es llevar el tirador de la derecha hasta el cero. Pulsar la cifra la deja escribir.
 */
function RangoImporte({
  limites,
  valor,
  activo,
  onChange,
}: {
  limites: [number, number];
  valor: [number, number];
  activo: boolean;
  onChange: (valor: [number, number] | null) => void;
}) {
  const [min, max] = limites;
  const [desde, hasta] = valor;
  const pct = (v: number) => ((v - min) / (max - min)) * 100;
  const corto = (v: number) => `${v < 0 ? '−' : ''}${Math.abs(v).toLocaleString('es-ES')} €`;

  function fijar(nuevoDesde: number, nuevoHasta: number) {
    const a = Math.max(min, Math.min(nuevoDesde, nuevoHasta));
    const b = Math.min(max, Math.max(nuevoDesde, nuevoHasta));
    onChange(a === min && b === max ? null : [a, b]);
  }

  return (
    <div className={activo ? `${styles.rango} ${styles.rangoActivo}` : styles.rango}>
      <span className={styles.rangoTitulo}>Importe</span>
      <NumeroRango valor={desde} onCommit={(v) => fijar(v, hasta)} etiqueta="Importe desde" />
      <div className={styles.rangoPista}>
        <div className={styles.rangoBarra} />
        <div
          className={styles.rangoSel}
          style={{ left: `${pct(desde)}%`, width: `${Math.max(0, pct(hasta) - pct(desde))}%` }}
        />
        {min < 0 && max > 0 && (
          <div className={styles.rangoCero} style={{ left: `${pct(0)}%` }} title="0 €" aria-hidden="true" />
        )}
        <input
          type="range"
          min={min}
          max={max}
          step={1}
          value={desde}
          onChange={(event) => fijar(Math.min(Number(event.target.value), hasta), hasta)}
          aria-label={`Importe mínimo: ${corto(desde)}`}
        />
        <input
          type="range"
          min={min}
          max={max}
          step={1}
          value={hasta}
          onChange={(event) => fijar(desde, Math.max(Number(event.target.value), desde))}
          aria-label={`Importe máximo: ${corto(hasta)}`}
        />
      </div>
      <NumeroRango valor={hasta} onCommit={(v) => fijar(desde, v)} etiqueta="Importe hasta" />
      <span className={styles.rangoEuro}>€</span>
    </div>
  );
}

/** Una cifra del abanico: se escribe libre y se aplica al salir o con Enter. */
function NumeroRango({
  valor,
  onCommit,
  etiqueta,
}: {
  valor: number;
  onCommit: (valor: number) => void;
  etiqueta: string;
}) {
  const [texto, setTexto] = useState(String(valor));
  useEffect(() => setTexto(String(valor)), [valor]);
  function aplicar() {
    const v = Number(texto.replace(/\./g, '').replace(',', '.').replace('−', '-'));
    if (texto.trim() !== '' && Number.isFinite(v)) onCommit(Math.round(v));
    else setTexto(String(valor));
  }
  return (
    <input
      type="text"
      inputMode="decimal"
      className={styles.rangoNum}
      value={texto}
      onChange={(event) => setTexto(event.target.value)}
      onBlur={aplicar}
      onKeyDown={(event) => {
        if (event.key === 'Enter') aplicar();
      }}
      aria-label={etiqueta}
    />
  );
}

/**
 * La conciliación en el móvil: una ficha por movimiento.
 *
 * Arriba, lo que decide si hay que tocarla —el concepto, el importe y el estado—; debajo, en
 * pequeño, el número, la fecha, el proveedor, el inmueble y el papel. Pulsar la ficha abre lo
 * mismo que pulsar la fila en la tabla.
 */
function MobileList({
  filas,
  total,
  numeros,
  onOpen,
}: {
  filas: FilaConciliacion[];
  total: number;
  numeros: Map<string, number>;
  onOpen: (fila: FilaConciliacion) => void;
}) {
  if (filas.length === 0) {
    return (
      <p className={styles.cardsEmpty}>
        {total === 0
          ? 'No hay movimientos este mes. Suelta los tickets en su inmueble o sube el extracto del banco.'
          : 'Ninguna fila con este filtro.'}
      </p>
    );
  }
  return (
    <ul className={styles.cards} aria-label="Movimientos del mes">
      {filas.map((fila) => {
        const clicable = Boolean(fila.entry || fila.movement || fila.payment || fila.prevista);
        const meta = [fila.proveedor, fila.inmueble ? nombreDelInmueble(fila) : null, fila.pagador && !fila.pagador.sugerido ? `Paga ${fila.pagador.name}` : null]
          .filter(Boolean)
          .join(' · ');
        return (
          <li
            key={fila.key}
            className={[styles.card, clicable ? styles.cardClickable : '', fila.prevista ? styles.cardPrevista : '']
              .filter(Boolean)
              .join(' ')}
            {...(clicable
              ? {
                  tabIndex: 0,
                  role: 'button',
                  onClick: () => onOpen(fila),
                  onKeyDown: (event: KeyboardEvent) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onOpen(fila);
                    }
                  },
                }
              : {})}
          >
            <div className={styles.cardTop}>
              <span className={styles.cardConcept}>{fila.concepto}</span>
              <span
                className={[
                  styles.cardAmount,
                  fila.importe !== null && fila.importe > 0 ? styles.income : '',
                ].join(' ')}
              >
                {fila.importe === null ? '—' : euros(fila.importe)}
              </span>
            </div>
            <div className={styles.cardMid}>
              <span className={styles.cardWhen}>
                Nº {numeros.get(fila.key)} · {fila.fecha ? formatShortDate(fila.fecha) : 'sin fecha'}
                {meta ? ` · ${meta}` : ''}
              </span>
              <span className={`${styles.estado} ${ESTADO[fila.estado].clase}`}>
                <span className={styles.dot} aria-hidden="true" />
                {fila.estado === 'sin_justificar' ? 'Sin justificar' : ESTADO[fila.estado].label}
              </span>
            </div>
            <div className={styles.cardFoot}>
              {fila.soporte === 'extracto' ? (
                <span className={styles.muted}>Extracto bancario</span>
              ) : fila.soporte ? (
                <DocLink document={fila.soporte} />
              ) : (
                <span className={styles.missingDoc}>Sin documento</span>
              )}
              {fila.saldo !== null && <span className={styles.cardSaldo}>Saldo {euros(fila.saldo)}</span>}
            </div>
            {fila.observaciones || fila.duda || fila.porLaIa ? (
              <p className={styles.cardNotes}>
                <Observaciones fila={fila} />
              </p>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Las observaciones de una fila: las de una persona y, de la IA, su DUDA —con su etiqueta, que es
 * lo que se busca al repasar— o la marca de que lo resolvió ella, con el porqué al pasar por encima.
 */
function Observaciones({ fila }: { fila: FilaConciliacion }) {
  return (
    <>
      {fila.duda && (
        <span className={styles.duda} title={`Duda: ${fila.duda}`}>
          <span className={styles.dudaTag}>Duda</span> {fila.duda}
        </span>
      )}
      {fila.cambioIa && (
        <span className={styles.duda} title={`La IA lo cambió. ${fila.cambioIa}`}>
          <span className={styles.dudaTag}>Cambio IA</span> {fila.cambioIa}
        </span>
      )}
      {fila.avisoIa && (
        <span className={styles.duda} title={`La IA cree que está mal: ${fila.avisoIa}`}>
          <span className={styles.dudaTag}>Aviso IA</span> {fila.avisoIa}
        </span>
      )}
      {fila.porLaIa && (
        <span className={styles.iaChip} title={`Lo decidió la IA: ${fila.porLaIa}`}>
          IA
        </span>
      )}
      {fila.observaciones && <span>{fila.observaciones}</span>}
    </>
  );
}

/** El documento, que se abre en el visor sin abrir la fila. */
function DocLink({ document }: { document: { id: string; name: string } }) {
  const visor = useDocumentViewer();
  return (
    <button
      type="button"
      className={styles.docLink}
      title={`Ver ${document.name}`}
      onClick={(event) => {
        event.stopPropagation();
        visor.open({ id: document.id, name: document.name });
      }}
    >
      <span>{document.name}</span>
    </button>
  );
}

/**
 * Quién paga un ingreso. Lo propuesto (por la IA o por las sugerencias) se enseña distinto, con
 * un «?», porque todavía no está guardado: se guarda al guardar la fila o desde su lápiz.
 */
function Pagador({ fila }: { fila: FilaConciliacion }) {
  const pagador = fila.pagador;
  if (!pagador) return <span className={styles.clip}>—</span>;
  if (!pagador.sugerido) return <span className={styles.clip}>{pagador.name}</span>;
  return (
    <span className={`${styles.clip} ${styles.pagadorSugerido}`} title={pagador.motivo ?? 'Propuesto, sin guardar'}>
      ¿{pagador.name}?
    </span>
  );
}

/** El inmueble de la fila tal como se lee en la tabla: con su habitación, si es de una sola. */
function nombreDelInmueble(fila: FilaConciliacion): string {
  if (!fila.inmueble) return '—';
  return fila.habitacion ? `${fila.inmueble.name} · ${fila.habitacion}` : fila.inmueble.name;
}
